import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Card,
  CardBody,
  HStack,
  VStack,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import {
  IoRefreshOutline,
  IoReloadOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoWarning,
  IoDocumentTextOutline,
} from "react-icons/io5";
import dayjs from "dayjs";

import Loader from "../components/loader";
import usePm2 from "../hooks/usePm2";
import { getPm2Status, restartPm2Process, getPm2Logs } from "../store/actions/pm2Actions";

const PM2Control = () => {
  const dispatch = useDispatch();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [restartDelay, setRestartDelay] = useState(2000);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    pm2Loading,
    pm2Error,
    pm2Restarting,
    hasData,
    hasError,
    isManaged,
    isHealthy,
    processName,
    processStatus,
    processPid,
    restartCount,
    statusColor,
    memoryMB,
    memoryPercent,
    cpuPercent,
    uptimeFormatted,
    lastUpdated,
    pm2Logs,
    pm2LogsLoading,
    hasLogs,
  } = usePm2();

  // Fetch PM2 status on mount
  useEffect(() => {
    dispatch(getPm2Status());
  }, [dispatch]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      dispatch(getPm2Status());
    }, 15000);

    return () => clearInterval(interval);
  }, [autoRefresh, dispatch]);

  const handleManualRefresh = () => {
    dispatch(getPm2Status());
  };

  const handleRestartConfirm = () => {
    onClose();
    dispatch(restartPm2Process(restartDelay, processName));
  };

  const handleFetchLogs = () => {
    dispatch(getPm2Logs());
  };

  const getStatusIcon = (isOk) => {
    return isOk ? (
      <IoCheckmarkCircle size={24} color="green" />
    ) : (
      <IoCloseCircle size={24} color="red" />
    );
  };

  const getMemoryColor = () => {
    if (memoryPercent > 80) return "red";
    if (memoryPercent > 60) return "yellow";
    return "green";
  };

  const getCpuColor = () => {
    if (cpuPercent > 80) return "red";
    if (cpuPercent > 50) return "yellow";
    return "green";
  };

  if (pm2Loading && !hasData) {
    return <Loader />;
  }

  return (
    <Box>
      <Helmet>
        <title>PM2 Process Control</title>
      </Helmet>

      {/* Header */}
      <HStack justifyContent="space-between" mb={4}>
        <Box>
          <Heading fontSize="34px" color="#222" fontWeight="700">
            PM2 Process Control
          </Heading>
          {lastUpdated && (
            <Text fontSize="sm" color="gray.600">
              Last updated: {dayjs(lastUpdated).format("MMM D, YYYY h:mm:ss A")}
            </Text>
          )}
        </Box>
        <HStack>
          <Button
            size="sm"
            variant={autoRefresh ? "solid" : "outline"}
            colorScheme={autoRefresh ? "blue" : "gray"}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh: {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button
            leftIcon={<IoRefreshOutline />}
            onClick={handleManualRefresh}
            isLoading={pm2Loading}
            size="sm"
            colorScheme="blue"
          >
            Refresh
          </Button>
          <Button
            leftIcon={<IoReloadOutline />}
            onClick={onOpen}
            isLoading={pm2Restarting}
            size="sm"
            colorScheme="orange"
            isDisabled={!hasData}
          >
            Restart Process
          </Button>
        </HStack>
      </HStack>

      {/* Error Alert */}
      {hasError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{pm2Error}</AlertDescription>
        </Alert>
      )}

      {/* Restarting Alert */}
      {pm2Restarting && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Restarting Process...</AlertTitle>
          <AlertDescription>
            PM2 process is restarting. This page will refresh automatically.
          </AlertDescription>
        </Alert>
      )}

      {/* Not Managed Alert */}
      {hasData && !isManaged && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertTitle>Process Not Managed by PM2</AlertTitle>
          <AlertDescription>
            This process is not currently managed by PM2. Control operations are not
            available.
          </AlertDescription>
        </Alert>
      )}

      {/* Process Status Card */}
      {hasData && isManaged && (
        <Card mb={6}>
          <CardBody>
            <HStack justifyContent="space-between" mb={4}>
              <Heading size="md">Process Status</Heading>
              {getStatusIcon(isHealthy)}
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Process Name
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  {processName}
                </Text>
              </VStack>

              <VStack align="stretch" spacing={2}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Status
                </Text>
                <Badge colorScheme={statusColor} fontSize="md" px={3} py={1}>
                  {processStatus.toUpperCase()}
                </Badge>
              </VStack>

              <VStack align="stretch" spacing={2}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Process ID
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  {processPid || "N/A"}
                </Text>
              </VStack>

              <VStack align="stretch" spacing={2}>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                  Restarts
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  {restartCount}
                </Text>
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Metrics Grid */}
      {hasData && isManaged && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          {/* Uptime */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Uptime</StatLabel>
                <StatNumber fontSize="2xl">{uptimeFormatted}</StatNumber>
                <StatHelpText>Time since last start</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Memory Usage */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Memory Usage</StatLabel>
                <StatNumber fontSize="2xl">{memoryMB} MB</StatNumber>
                <StatHelpText>
                  <Progress
                    value={memoryPercent}
                    colorScheme={getMemoryColor()}
                    size="sm"
                    mt={2}
                    borderRadius="md"
                  />
                  <Text mt={1}>{memoryPercent.toFixed(1)}% of 512 MB</Text>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* CPU Usage */}
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>CPU Usage</StatLabel>
                <StatNumber fontSize="2xl">{cpuPercent}%</StatNumber>
                <StatHelpText>
                  <Progress
                    value={cpuPercent}
                    colorScheme={getCpuColor()}
                    size="sm"
                    mt={2}
                    borderRadius="md"
                  />
                  <Text mt={1}>Current utilization</Text>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Process Information */}
      {hasData && isManaged && (
        <Card>
          <CardBody>
            <Heading size="md" mb={4}>
              Process Information
            </Heading>
            <VStack align="stretch" spacing={3}>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Total Restarts:</Text>
                <Badge colorScheme={restartCount > 5 ? "red" : "green"} fontSize="md">
                  {restartCount}
                </Badge>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Health Status:</Text>
                <Badge colorScheme={isHealthy ? "green" : "red"} fontSize="md">
                  {isHealthy ? "Healthy" : "Unhealthy"}
                </Badge>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Managed by PM2:</Text>
                <Badge colorScheme="blue" fontSize="md">
                  Yes
                </Badge>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* PM2 Logs Section */}
      <Box mt={6}>
        <HStack justifyContent="space-between" mb={4}>
          <Heading size="md">PM2 Logs</Heading>
          <Button
            leftIcon={<IoDocumentTextOutline />}
            onClick={handleFetchLogs}
            isLoading={pm2LogsLoading}
            size="sm"
            colorScheme="purple"
          >
            Fetch Logs
          </Button>
        </HStack>

        {hasLogs && (
          <Card>
            <CardBody>
              <Box
                bg="gray.900"
                color="green.300"
                p={4}
                borderRadius="md"
                fontFamily="monospace"
                fontSize="sm"
                maxH="500px"
                overflowY="auto"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {pm2Logs.out && (
                  <Box mb={4}>
                    <Text fontWeight="bold" color="blue.300" mb={2}>
                      STDOUT:
                    </Text>
                    <Text color="green.300">{pm2Logs.out}</Text>
                  </Box>
                )}
                {pm2Logs.err && (
                  <Box>
                    <Text fontWeight="bold" color="red.300" mb={2}>
                      STDERR:
                    </Text>
                    <Text color="red.400">{pm2Logs.err}</Text>
                  </Box>
                )}
                {!pm2Logs.out && !pm2Logs.err && (
                  <Text color="gray.500">No logs available</Text>
                )}
              </Box>
            </CardBody>
          </Card>
        )}

        {!hasLogs && !pm2LogsLoading && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              Click "Fetch Logs" to view PM2 process logs
            </AlertDescription>
          </Alert>
        )}
      </Box>

      {/* Restart Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Process Restart</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Warning</AlertTitle>
                  <AlertDescription>
                    This will restart the PM2 process. The SMS server will be temporarily
                    unavailable during the restart.
                  </AlertDescription>
                </Box>
              </Alert>

              <FormControl>
                <FormLabel>Restart Delay (milliseconds)</FormLabel>
                <Input
                  type="number"
                  value={restartDelay}
                  onChange={(e) => setRestartDelay(parseInt(e.target.value) || 2000)}
                  min={1000}
                  max={10000}
                  step={1000}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Delay before restart: {restartDelay / 1000} seconds
                </Text>
              </FormControl>

              <Text fontSize="sm" color="gray.600">
                Process: <strong>{processName}</strong>
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="orange" onClick={handleRestartConfirm}>
              Restart Process
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PM2Control;
